<template>
    <QField
        v-if="!iconOnly"
        ref="fieldRef"
        :model-value="fieldModelValue"
        :rules="rules"
        :style="{ minWidth: width || allowTime ? '460px' : '240px', maxWidth: width || '460px' }"
        :class="fieldClass"
        :label="label"
        :disable="!isactive"
        :hint="messageValue"
        :hide-bottom-space="hideBottomSpaceValue"
        :bottom-slots="bottomSlotsValue"
        hide-hint
        outlined
        dense
        :readonly="readonly"
        @keydown="onMainFieldKeydown"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
    >
        <template #prepend>
            <QIcon
                :name="isactive ? (activeicon || 'mdi-calendar') : (inactiveicon || 'mdi-calendar-remove')"
                style="color: var(--v-secondaryText-base);"
                class="cursor-pointer"
                @click.stop="toggleDatePicker"
            />
            <QTooltip v-if="showtooltip">
                <span>{{ isactive ? Translate('Date range picker (Active)') : Translate('Date range picker (Inactive)') }}</span>
            </QTooltip>
        </template>

        <template #control>
            <div class="self-center full-width no-outline" tabindex="0">
                {{ displaydaterange }}
            </div>
        </template>

        <QMenu
            v-model="datePickerOpen"
            :disable="readonly"
            :close-on-content-click="false"
            anchor="bottom left"
            self="top left"
        >
            <QCard>
                <div class="row no-wrap">
                    <QList dense class="calendar-presets q-ml-sm q-mt-md">
                        <QItem
                            v-for="p in presets"
                            :key="p.label"
                            v-ripple
                            clickable
                            :active="preset_choice === p.label"
                            active-class="bg-primary text-white"
                            @click="setRange(p.range); preset_choice = p.label"
                        >
                            <QItemSection>
                                <QItemLabel>{{ p.label }}</QItemLabel>
                            </QItemSection>
                        </QItem>
                    </QList>
                    <div class="column calendar-picker-block">
                        <CalendarRange
                            :start-date="calendarRangeStartIso"
                            :end-date="calendarRangeEndIso"
                            :prevent-future="!allowFutureDate"
                            :prevent-past="!allowPastDate"
                            :pane-gap="38"
                            :padding-left="12"
                            @change="onCalendarRangeChange"
                        />
                        <div class="row no-wrap q-gutter-sm q-mt-sm calendar-input-columns">
                            <div class="column calendar-input-column">
                                <div class="row no-wrap q-gutter-xs calendar-inner-row">
                                    <QInput
                                        v-model="date_1_text"
                                        dense
                                        outlined
                                        hide-bottom-space
                                        type="date"
                                        :max="nativeDateMax"
                                        :min="nativeDateMin"
                                        class="calendar-inner-date"
                                        @update:model-value="date_1_text_input"
                                        @change="date_1_text_change"
                                    />
                                    <QInput
                                        v-if="allowTime"
                                        v-model="time_1"
                                        dense
                                        outlined
                                        hide-bottom-space
                                        type="time"
                                        step="1"
                                        class="calendar-inner-time"
                                    />
                                </div>
                            </div>
                            <div class="column calendar-input-column">
                                <div class="row no-wrap q-gutter-xs calendar-inner-row">
                                    <QInput
                                        v-model="date_2_text"
                                        dense
                                        outlined
                                        hide-bottom-space
                                        type="date"
                                        :max="nativeDateMax"
                                        :min="nativeDateMin"
                                        class="calendar-inner-date"
                                        @update:model-value="date_2_text_input"
                                        @change="date_2_text_change"
                                    />
                                    <QInput
                                        v-if="allowTime"
                                        v-model="time_2"
                                        dense
                                        outlined
                                        hide-bottom-space
                                        type="time"
                                        step="1"
                                        class="calendar-inner-time"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <QCardActions class="q-pt-md" style="flex-flow: row-reverse; padding-right: 25px; padding-bottom: 15px;">
                    <QBtn flat no-caps class="c-SentenceCaseButton" color="primary" :disable="!isvalid" @click="save()">
                        <span>{{ Translate('Apply') }}</span>
                    </QBtn>
                    <QBtn flat no-caps class="c-SentenceCaseButton" @click="datePickerOpen = false">
                        <span>{{ Translate('Cancel') }}</span>
                    </QBtn>
                    <QSpace />
                    <span v-if="isafter" class="text-weight-medium q-ml-md" style="color: maroon;">Warning - start is later than end</span>
                </QCardActions>
            </QCard>
        </QMenu>
    </QField>
</template>

<script>
    import {
        QField, QInput, QIcon, QBtn, QMenu, QCard, QCardActions, QList, QItem, QItemSection, QItemLabel,
        QTooltip, QSpace,
    } from 'quasar';
    import utils from '@/Shared/utils.jsx';
    import filters from '@/Shared/filters.js';
    import methods from '@/Shared/methods';
    import CalendarRange from './CalendarRange.vue';
    import dayjs from 'dayjs';

    export default {
        name: 'Calendar',
        components: {
            QField, QInput, QIcon, QBtn, QMenu, QCard, QCardActions, QList, QItem, QItemSection, QItemLabel,
            QTooltip, QSpace, CalendarRange,
        },
        data() {
            const now = dayjs();
            return {
                daterange: {
                    // Default to start of last month to end of this month
                    //startDate: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD') + 'T00:00:00',
                    //endDate: now.endOf('month').format('YYYY-MM-DD') + 'T23:59:59',

                    // Default to today
                    startDate: now.format('YYYY-MM-DD') + 'T00:00:00',
                    endDate: now.format('YYYY-MM-DD') + 'T23:59:59',
                },
                datePickerOpen: false,
                preset_choice: null,

                date_1_text: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                date_2_text: now.format('YYYY-MM-DD'),

                valid_calendar_1: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                valid_calendar_2: now.format('YYYY-MM-DD'),

                time_1: '00:00:00',
                time_2: '23:59:59',

                isafter: false,
            };
        },
        props: {
            value: {
                type: [Object, String, Date],
                default: null,
            },
            modelValue: {
                default: undefined,
            },
            rules: {
                type: Array,
                default: () => [],
            },
            label: { type: String, default: null },
            width: { type: String, default: null },
            iconOnly: { type: Boolean, default: false },
            activeicon: { type: String, default: null },
            inactiveicon: { type: String, default: null },
            activecolor: { type: String, default: null },
            inactivecolor: { type: String, default: null },
            showtooltip: { type: Boolean, default: false },
            position: { type: String, default: null },
            myclass: { type: String, default: null },
            isactive: { type: Boolean, default: true },
            allowFutureDate: { type: Boolean, default: true },
            allowPastDate: { type: Boolean, default: true },
            message: { type: String, default: null },
            allowTime: { type: Boolean, default: true },
            readonly: { type: Boolean, default: false },
            // When set, overrides the default (!message) bottom-space behavior.
            hideBottomSpace: { type: Boolean, default: undefined },
            bottomSlots: { type: Boolean, default: undefined },
        },
        emits: ['change', 'blur', 'focus'],
        created() {
            if (this.value) {
                if (typeof this.value === 'string') {
                    try {
                        const valueobj = JSON.parse(this.value);
                        this.daterange = { ...this.daterange, ...valueobj };
                    } catch (_) {}
                } else {
                    this.daterange = { ...this.daterange, ...this.value };
                }
            }
        },
        watch: {
            value(newv) {
                this.datePickerOpen = false;
                if (newv == null) return;
                if (typeof newv === 'string') {
                    try {
                        const valueobj = JSON.parse(newv);
                        this.daterange = { ...this.daterange, ...valueobj };
                    } catch (_) {}
                } else {
                    this.daterange = { ...this.daterange, ...newv };
                }
            },
            datePickerOpen(newv) {
                if (newv) {
                    this.preset_choice = null;
                    const range1 = dayjs(this.daterange.startDate);
                    const range2 = dayjs(this.daterange.endDate);
                    const from = range1.format('YYYY-MM-DD');
                    const to = range2.format('YYYY-MM-DD');
                    
                    this.date_1_text = from;
                    this.date_2_text = to;
                    
                    this.valid_calendar_1 = from;
                    this.valid_calendar_2 = to;
                    
                    this.time_1 = range1.format('HH:mm:ss');
                    this.time_2 = range2.format('HH:mm:ss');
                }
            },
            date_1_text(newv) {
                if (!newv) return;
                const year = parseInt(newv.split('-')[0], 10);
                if (year >= 1000) this.valid_calendar_1 = newv;
            },
            date_2_text(newv) {
                if (!newv) return;
                const year = parseInt(newv.split('-')[0], 10);
                if (year >= 1000) this.valid_calendar_2 = newv;
            }
        },
        computed: {
            fieldClass() {
                return [this.myclass, 'c-calendar-field'].filter(Boolean);
            },
            // QField uses model-value to float the label. Callers like AdvancedReports pass
            // :value for the range object but not modelValue, while the visible text lives in
            // displaydaterange — bind that so the label does not overlap the control slot.
            fieldModelValue() {
                return this.displaydaterange;
            },
            messageValue() {
                if (this.message)
                    return this.message;
            },
            hideBottomSpaceValue() {
                if (typeof this.hideBottomSpace === 'boolean')
                    return this.hideBottomSpace;
                return !this.message;
            },
            bottomSlotsValue() {
                if (typeof this.bottomSlots === 'boolean')
                    return this.bottomSlots;
                return !!this.message;
            },
            presetsTranslated() {
                const t = (key) => (this.Translate ? this.Translate(key) : key);
                const now = dayjs();
                return [
                    { label: t('Today'), range: [now.startOf('day'), now.endOf('day')] },
                    { label: t('Yesterday'), range: [now.subtract(1, 'day').startOf('day'), now.subtract(1, 'day').endOf('day')] },
                    { label: t('Last 7 Days'), range: [now.subtract(6, 'day').startOf('day'), now.endOf('day')] },
                    { label: t('Last 30 Days'), range: [now.subtract(29, 'day').startOf('day'), now.endOf('day')] },
                    { label: t('This Month'), range: [now.startOf('month'), now.endOf('month')] },
                    { label: t('Last Month'), range: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
                    { label: t('Last 3 Months'), range: [now.subtract(3, 'month').startOf('month'), now.endOf('month')] },
                    { label: t('Last 6 Months'), range: [now.subtract(6, 'month').startOf('month'), now.endOf('month')] },
                    { label: t('Last 9 Months'), range: [now.subtract(9, 'month').startOf('month'), now.endOf('month')] },
                    { label: t('Last 1 Year'), range: [now.subtract(12, 'month').startOf('month'), now.endOf('month')] },
                ];
            },
            presets() {
                return this.presetsTranslated;
            },
            displaydaterange() {
                const dj = dayjs(new Date(this.daterange.startDate));
                if (this.allowTime) {
                    return `${this.format(this.daterange.startDate)} - ${this.format(this.daterange.endDate)}`;
                } else {
                    return `${dj.format('L')} - ${dayjs(new Date(this.daterange.endDate)).format('L')}`;
                }
            },
            calendarRangeStartIso() {
                if (!this.valid_calendar_1) return null;
                return `${this.valid_calendar_1}T00:00:00.000Z`;
            },
            calendarRangeEndIso() {
                if (!this.valid_calendar_2) return null;
                return `${this.valid_calendar_2}T00:00:00.000Z`;
            },
            isvalid() {
                if (!this.date_1_text || !this.date_2_text) return false;
                const date1 = `${this.date_1_text}T${this.time_1}`;
                const date2 = `${this.date_2_text}T${this.time_2}`;
                return dayjs(date1).isValid() && dayjs(date2).isValid();
            },
            nativeDateMin() {
                if (this.allowPastDate) return '1979-01-01';
                return dayjs().format('YYYY-MM-DD');
            },
            nativeDateMax() {
                if (this.allowFutureDate) return '2099-12-31';
                return dayjs().format('YYYY-MM-DD');
            },
        },
        methods: {
            ...methods,
            validate() {
                return this.$refs.fieldRef?.validate?.() ?? Promise.resolve(true);
            },
            openDatePicker() {
                if (this.readonly || !this.isactive)
                    return;
                this.datePickerOpen = true;
            },
            toggleDatePicker(e) {
                if (this.readonly || !this.isactive)
                    return;
                e?.stopPropagation?.();
                this.datePickerOpen = !this.datePickerOpen;
            },
            onMainFieldKeydown(e) {
                if (this.readonly) return;
                const key = e.key;
                if (key === 'Tab' || key === 'Escape') return;
                if (key.startsWith('Arrow') || key === 'Home' || key === 'End' || key === 'PageUp' || key === 'PageDown') return;
                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'x'].includes(key.toLowerCase())) return;
                if (key === 'Backspace' || key === 'Delete') {
                    e.preventDefault();
                    return;
                }
                if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === 'v') {
                    e.preventDefault();
                    return;
                }
                if (key.length === 1 && !e.ctrlKey && !e.metaKey) e.preventDefault();
            },
            format(datevalue) {
                const dj = dayjs(new Date(datevalue));
                if (this.allowTime) {
                    return dj.format('L') + ' ' + dj.format('LTS');
                } else {
                    return dj.format('L');
                }
            },
            isoToYmd(isoValue) {
                if (!isoValue || typeof isoValue !== 'string') return null;
                const match = isoValue.match(/^(\d{4}-\d{2}-\d{2})/);
                if (match) return match[1];
                const parsed = dayjs(isoValue);
                return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
            },
            onCalendarRangeChange(newRange) {
                if (!newRange || typeof newRange !== 'object') return;
                const from = this.isoToYmd(newRange.startDate);
                const to = this.isoToYmd(newRange.endDate);
                if (!from) return;
                // Keep end date unchanged during partial selection (start picked, end not picked yet).
                // This avoids forcing the right pane to jump months while the range is still being chosen.
                if (!to) {
                    this.date_1_text = from;
                    this.isafter = this.date_2_text ? dayjs(this.date_1_text).isAfter(dayjs(this.date_2_text)) : false;
                    this.preset_choice = null;
                    return;
                }
                let normalizedFrom = from;
                let normalizedTo = to;
                if (dayjs(normalizedFrom).isAfter(dayjs(normalizedTo))) {
                    [normalizedFrom, normalizedTo] = [normalizedTo, normalizedFrom];
                }
                this.date_1_text = normalizedFrom;
                this.date_2_text = normalizedTo;
                this.isafter = false;
                this.preset_choice = null;
            },
            setRange(range) {
                const r0 = dayjs(range[0]);
                const r1 = dayjs(range[1]);
                const from = r0.format('YYYY-MM-DD');
                const to = r1.format('YYYY-MM-DD');
                this.date_1_text = from;
                this.date_2_text = to;
                this.time_1 = '00:00:00';
                this.time_2 = '23:59:59';
                this.isafter = dayjs(from).isAfter(dayjs(to));
            },
            date_1_text_input() {
                this.preset_choice = null;
            },
            date_2_text_input() {
                this.preset_choice = null;
            },
            date_1_text_change(v) {
                if (!v) return;
                this.preset_choice = null;

                // Check if the year is in the process of being typed
                const year = parseInt(v.split('-')[0], 10);
                if (year > 0 && year < 1000) return;

                if (dayjs(v).isAfter(dayjs('2099-12-31'))) this.date_1_text = '2099-12-31';
                else if (dayjs(v).isBefore(dayjs('1979-01-01'))) this.date_1_text = '1979-01-01';

                this.date_1_text = this.date_1_text || v;
                if (this.date_2_text && dayjs(this.date_1_text).isAfter(dayjs(this.date_2_text))) {
                    this.date_1_text = this.date_2_text;
                }
                this.isafter = dayjs(this.date_1_text).isAfter(dayjs(this.date_2_text));
            },
            date_2_text_change(v) {
                if (!v) return;
                this.preset_choice = null;

                const year = parseInt(v.split('-')[0], 10);
                if (year > 0 && year < 1000) return;

                if (dayjs(v).isAfter(dayjs('2099-12-31'))) this.date_2_text = '2099-12-31';
                else if (dayjs(v).isBefore(dayjs('1979-01-01'))) this.date_2_text = '1979-01-01';

                this.date_2_text = this.date_2_text || v;
                if (this.date_1_text && dayjs(this.date_1_text).isAfter(dayjs(this.date_2_text))) {
                    this.date_1_text = this.date_2_text;
                }
                this.isafter = dayjs(this.date_1_text).isAfter(dayjs(this.date_2_text));
            },
            save() {
                this.preset_choice = null;
                const t1 = this.allowTime ? this.time_1 : '00:00:00';
                const t2 = this.allowTime ? this.time_2 : '23:59:59';
                const date1 = `${this.date_1_text}T${t1}`;
                const date2 = `${this.date_2_text}T${t2}`;
                const newvalue = { startDate: date1, endDate: date2 };
                this.daterange = { ...this.daterange, ...newvalue };
                this.datePickerOpen = false;
                this.$emit('change', newvalue);
            },
        },
    };
</script>

<style scoped>
    .calendar-presets {
        min-width: 140px;
    }
    .calendar-picker-block {
        padding: 15px;
        min-width: 620px;
    }
    .calendar-input-columns {
        width: 100%;
    }
    .calendar-input-column {
        flex: 1 1 0;
        min-width: 0;
    }
    .calendar-inner-row {
        width: 100%;
    }
    .calendar-inner-date,
    .calendar-inner-time {
        flex: 1 1 0;
        min-width: 0;
    }
    .calendar-inner-date :deep(.q-field__control),
    .calendar-inner-time :deep(.q-field__control) {
        border: 1px solid rgba(0, 0, 0, 0.24);
        border-radius: 8px;
        padding-right: 5px;
    }
</style>